import React, { useContext, useEffect, useState } from "react";
import { useRoutes } from "react-router-dom";
import { Provider } from "react-redux";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "./i18n";
import createTheme from "./theme";
import routes from "./routes";
import useTheme from "./hooks/useTheme";
import { store } from "./redux/store";
import createEmotionCache from "./utils/createEmotionCache";
import { initializeFileTypeIcons } from "@fluentui/react-file-type-icons";
import SignIn from "./components/auth/SignIn";
import { useAuth } from "./components/auth/AuthContext";
import { GlobalContext } from "./contexts/GlobalContext";
import LoadingSpinner from "./components/LoadingSpinner";

initializeFileTypeIcons();

const clientSideEmotionCache = createEmotionCache();

function App({ emotionCache = clientSideEmotionCache }) {
  const { theme } = useTheme();
  const auth = useAuth();
  const { isAuthenticated } = auth;
  const { fetchUserData } = useContext(GlobalContext);
  const content = useRoutes(routes);

  const [authState, setAuthState] = useState("checking");

  useEffect(() => {
    if (!isAuthenticated) {
      setAuthState("unauthorized");
      return;
    }

    let cancelled = false;

    setAuthState("checking");
    // console.log("User authenticated, fetching user data...");

    const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Request timeout")), 10000));

    Promise.race([fetchUserData(), timeoutPromise])
      .then((authorized) => {
        if (cancelled) {
          return;
        }
        // console.log("User data fetched successfully, checking authorization...");
        // Use the boolean returned from fetchUserData — not isAuthorized from closure (stale until re-render).
        setAuthState(authorized === true ? "authorized" : "unauthorized");
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error("Error fetching user data:", error);
        if (error.message === "Request timeout") {
          console.error("User data fetch timed out");
        }
        setAuthState("unauthorized");
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, fetchUserData]);

  const renderContent = () => {
    switch (authState) {
      case "checking":
        return <LoadingSpinner />;
      case "unauthorized":
        return <SignIn />;
      case "authorized":
        return content;
      default:
        return <div>Unexpected state</div>;
    }
  };

  return (
    <CacheProvider value={emotionCache}>
      <HelmetProvider>
        <Helmet titleTemplate="%s | Delta Customer Portal" defaultTitle="Customer Portal - Delta Refund Services" />
        <Provider store={store}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MuiThemeProvider theme={createTheme(theme)}>{renderContent()}</MuiThemeProvider>
          </LocalizationProvider>
        </Provider>
      </HelmetProvider>
    </CacheProvider>
  );
}

export default App;
